<ng-container *ngIf="activeFormForTemplate as activeForm">
  <div class="filter-check sale-form-stack" [formGroup]="activeForm">

    <!-- Secção: Negócio associado -->
    <section *ngIf="!lockDealSelection" class="sale-section">
      <div class="sale-section-head">
        <div class="sale-section-icon"><i class="ri-briefcase-line"></i></div>
        <div class="min-w-0">
          <h6 class="sale-section-title">{{ 't-sale-form-associated-deal' | translate }}</h6>
          <p class="sale-section-sub">{{ 't-new-sales.t-associated-deal-sub' | translate }}</p>
        </div>
      </div>

      <div class="border rounded-3 p-3 bg-light">
        <div *ngIf="getSelectedDeal() as selectedDeal; else noDealSelected"
          class="border rounded-3 p-3 bg-white cursor-pointer" (click)="openDealPicker()">
          <div class="d-flex justify-content-between align-items-start gap-3">
            <div>
              <div class="fw-semibold">{{ getDealContactName(selectedDeal) }}</div>
              <div class="small text-muted">{{ 't-reference' | translate }}: {{ getDealPropertyRef(selectedDeal) }}
              </div>
              <div class="small text-muted">{{ getDealPropertyTitle(selectedDeal) }}</div>
              <div class="small text-muted">{{ getDealContactEmail(selectedDeal) }} | {{
                getDealContactPhone(selectedDeal) }}</div>
              <div class="small text-muted" *ngIf="getDealReceivedDate(selectedDeal)">
                {{ 't-sale-form-received-at' | translate }}: {{ getDealReceivedDate(selectedDeal) | date: 'dd/MM/yyyy HH:mm' }}
              </div>
            </div>

            <div class="text-end">
              <span class="badge badge-soft-primary" *ngIf="getDealStatusName(selectedDeal) !== '-'">{{ getDealStatusName(selectedDeal) }}</span>
              <div class="d-flex align-items-center justify-content-end gap-2 mt-2">
                <img [src]="getDealAgentImage(selectedDeal)" alt="avatar" class="rounded-circle" width="24"
                  height="24" />
                <span class="small text-muted">{{ getDealAgentName(selectedDeal) }}</span>
              </div>
            </div>
          </div>
        </div>

        <ng-template #noDealSelected>
          <button type="button" class="btn btn-outline-primary btn-sm" (click)="openDealPicker()">
            {{ 't-sale-form-select-deal' | translate }}
          </button>
        </ng-template>

        <div *ngIf="getSelectedDeal() && !isSelectedDealRelatedToCurrentProperty()"
          class="alert alert-warning py-2 px-3 mt-2 mb-0">
          {{ 't-sale-form-unrelated-warning' | translate }}
        </div>

        <div *ngIf="getSelectedDeal() && !lockDealSelection" class="mt-2 d-flex gap-2">
          <button type="button" class="btn btn-light btn-sm" (click)="openDealPicker()">
            {{ 't-edit' | translate }}
          </button>

          <button *ngIf="getSelectedDeal()" type="button" class="btn btn-outline-danger btn-sm"
            (click)="clearSelectedDeal()">
            {{ 't-remove' | translate }}
          </button>
        </div>
      </div>
      <div *ngIf="isDealRequiredInvalid()" class="invalid-feedback d-block">
        {{ 't-sale-form-select-deal-to-continue' | translate }}
      </div>
    </section>

    <!-- Secção: Dados da venda -->
    <section class="sale-section">
      <div class="sale-section-head">
        <div class="sale-section-icon"><i class="ri-money-euro-circle-line"></i></div>
        <div class="min-w-0">
          <h6 class="sale-section-title">{{ (isRentMode ? "t-new-sales.t-rent-data-title" : "t-new-sales.t-sale-data-title") | translate }}</h6>
          <p class="sale-section-sub">{{ (isRentMode ? "t-new-sales.t-rent-data-sub" : "t-new-sales.t-sale-data-sub") | translate }}</p>
        </div>
      </div>

      <div class="row g-3">
        <div class="col-12 col-md-6">
          <label for="saleValueInput" class="form-label">
            {{ (isRentMode ? 't-sale-form-rent-value' : 't-m-properties.t-sale-value') | translate }} <span class="text-danger">*</span>
          </label>
          <div class="input-group">
            <span class="input-group-text">{{ getSuffix() }}</span>
            <input [attr.disabled]="isLocked ? true : null" type="text" mask="separator" thousandSeparator=" "
              class="form-control" id="saleValueInput" autocomplete="off"
              [placeholder]="isRentMode ? ('t-sale-form-rent-value-placeholder' | translate) : ('t-enter' | translate) + ' ' + ('t-m-properties.t-sale-value' | translate)"
              formControlName="saleValue" />
          </div>
        </div>

        <div class="col-12 col-md-6">
          <label class="form-label">{{ (isRentMode ? 't-sale-form-rent-date' : 't-m-properties.t-sale-date') | translate }} <span class="text-danger">*</span></label>
          <input [attr.disabled]="isSaleDateLocked ? true : null" [readonly]="isSaleDateLocked"
            [ngStyle]="{ 'pointer-events': isSaleDateLocked ? 'none' : null }"
            [ngClass]="{ 'bg-light': isSaleDateLocked }" class="form-control flatpickr-input" type="text"
            placeholder="{{ 't-general.t-select' | translate }}" mwlFlatpickr [convertModelValue]="true"
            [dateFormat]="'Y-m-d'" [minDate]="saleDateMinDate" formControlName="saleDate" />
        </div>

        <!-- Inventário: só com imóvel nosso — num imóvel externo não há onde o guardar. -->
        <div *ngIf="!isRentMode && !isExternalProperty" class="col-12 col-md-6">
          <label for="inventoryValueInput" class="form-label">
            {{ "t-m-properties.t-inventory-value" | translate }}
          </label>
          <div class="input-group">
            <span class="input-group-text">{{ getSuffix() }}</span>
            <input [attr.disabled]="isLocked ? true : null" type="text" mask="separator" thousandSeparator=" "
              class="form-control" id="inventoryValueInput" autocomplete="off"
              placeholder="{{ 't-enter' | translate }} {{ 't-m-properties.t-inventory-value' | translate }}"
              formControlName="inventoryValue" />
          </div>
        </div>

        <div class="col-12 col-md-6">
          <div class="d-md-flex gap-4">
            <div>
              <label class="form-label">
                {{ "t-commission-type" | translate }}
              </label>

              <div class="form-check mb-1">
                <input [attr.disabled]="isLocked ? true : null" class="form-check-input" [value]="true" type="radio"
                  name="isPercentageCommission" id="isPercentageCommissionTrue" formControlName="isPercentageCommission" />

                <label class="form-check-label" for="isPercentageCommissionTrue">
                  {{ !isRentMode ? ("t-percentage" | translate) : ("t-months-m" | translate)}}
                </label>
              </div>

              <div class="form-check">
                <input [attr.disabled]="isLocked ? true : null" class="form-check-input" [value]="false" type="radio"
                  name="isPercentageCommission" id="isPercentageCommissionFalse" formControlName="isPercentageCommission" />

                <label class="form-check-label" for="isPercentageCommissionFalse">
                  {{ "t-fixed-value" | translate }}
                </label>
              </div>
            </div>

            <div class="flex-fill">
              <label for="saleCommissionInput" class="form-label">
                {{ "t-m-properties.t-sale-commission" | translate }}
              </label>

              <input [attr.disabled]="isLocked ? true : null" *ngIf="form['isPercentageCommission']?.value && !isRentMode"
                type="text" class="form-control" suffix="%" id="saleCommissionInput" autocomplete="off" mask="separator"
                thousandSeparator=" "
                placeholder="{{ 't-enter' | translate }} {{ 't-m-properties.t-sale-commission' | translate }}"
                formControlName="saleCommission" />

              <input [attr.disabled]="isLocked ? true : null" *ngIf="form['isPercentageCommission']?.value && isRentMode"
                type="number" class="form-control" id="saleCommissionInputRent" autocomplete="off"
                placeholder="{{ 't-enter' | translate }} {{ 't-m-properties.t-sale-commission' | translate }}"
                formControlName="saleCommission" />

              <input [attr.disabled]="isLocked ? true : null" *ngIf="!form['isPercentageCommission']?.value" type="text"
                class="form-control" id="saleCommissionInputFixed" suffix=" {{ getSuffix() }}" autocomplete="off"
                mask="separator" thousandSeparator=" "
                placeholder="{{ 't-enter' | translate }} {{ 't-m-properties.t-sale-commission' | translate }}"
                formControlName="saleCommission" />
            </div>
          </div>
        </div>

        <!-- Imóvel externo: só a comissão da parceira -->
        <div *ngIf="isExternalProperty" class="col-12 col-md-6">
          <label for="sharedCommission" class="form-label">
            {{ "t-partner-commission" | translate }}
          </label>
          <input [attr.disabled]="isLocked ? true : null" type="text" id="sharedCommission" mask="separator"
            thousandSeparator=" " suffix="%" placeholder="{{ 't-partner-commission' | translate }}"
            class="form-control" formControlName="sharedCommission" />
        </div>

        <!-- Imóvel externo: confirmar a agência que detém o imóvel (nome + licença
             ficam na venda externa). Só quando o formulário dono traz os controlos. -->
        <ng-container *ngIf="isExternalProperty && form['externalAgencyName']">
          <div class="col-12">
            <p class="text-muted fs-12 mb-0">{{ "t-new-sales.t-external-agency-hint" | translate }}</p>
          </div>
          <div class="col-12 col-md-6">
            <label for="externalAgencyName" class="form-label">
              {{ "t-new-sales.t-external-agency-name" | translate }} <span class="text-danger">*</span>
            </label>
            <input [attr.disabled]="isLocked ? true : null" type="text" id="externalAgencyName"
              placeholder="{{ 't-new-sales.t-external-agency-name' | translate }}" class="form-control"
              [class.is-invalid]="form['externalAgencyName']?.invalid && form['externalAgencyName']?.touched"
              formControlName="externalAgencyName" />
          </div>
          <div class="col-12 col-md-6">
            <label for="externalAgencyLicense" class="form-label">
              {{ "t-new-sales.t-external-agency-license" | translate }}
            </label>
            <input [attr.disabled]="isLocked ? true : null" type="text" id="externalAgencyLicense"
              placeholder="{{ 't-new-sales.t-external-agency-license' | translate }}" class="form-control"
              formControlName="externalAgencyLicense" />
          </div>
        </ng-container>
      </div>

      <ng-container *ngIf="!isExternalProperty">
        <div class="mt-3">
          <label for="SwitchShared" class="form-label">
            {{ "t-shared-sale" | translate }}
          </label>
          <div class="d-flex align-items-center">
            <div class="form-check form-check-inline form-switch form-switch-primary me-2">
              <input [attr.disabled]="isLocked ? true : null" class="form-check-input form-switch-md" type="checkbox"
                role="switch" id="SwitchShared" formControlName="sharedSale" />
            </div>
          </div>
        </div>

        <div class="sale-subblock mt-3" *ngIf="form['sharedSale']?.value">
          <div class="mb-3">
            <label class="form-label d-block">{{ "t-shared-sale-type" | translate }}</label>
            <div class="form-check form-check-inline">
              <input class="form-check-input" type="radio" id="shareTypeExternal" value="external"
                formControlName="shareType" [attr.disabled]="isLocked || isInternalShareLocked ? true : null" />
              <label class="form-check-label" for="shareTypeExternal">
                {{ "t-shared-sale-external" | translate }}
              </label>
            </div>
            <div class="form-check form-check-inline">
              <input class="form-check-input" type="radio" id="shareTypeInternal" value="internal"
                formControlName="shareType" [attr.disabled]="isLocked || isInternalShareLocked ? true : null" />
              <label class="form-check-label" for="shareTypeInternal">
                {{ "t-shared-sale-internal" | translate }}
              </label>
            </div>
          </div>

          <div class="row g-3 mb-3" *ngIf="isInternalShareMode">
            <div class="col-12">
              <label class="form-label">{{ "t-internal-share-store" | translate }}</label>
              <ng-select [items]="internalShareStores" bindValue="id" bindLabel="name"
                [loading]="loadingInternalShareTargets" [clearable]="true"
                placeholder="{{ 't-internal-share-select-store' | translate }}" formControlName="partnerStoreId"
                [readonly]="isLocked || isInternalShareLocked">
              </ng-select>
              <div class="text-danger small mt-1"
                *ngIf="form['partnerStoreId']?.invalid && (form['partnerStoreId']?.touched || submitedCheck)">
                {{ "t-internal-share-select-store" | translate }}
              </div>
            </div>
            <!-- Vendedor da loja parceira: opcional. A % dele não se pede —
                 entra a comissão de venda por defeito do utilizador. -->
            <div class="col-12">
              <label class="form-label">{{ "t-internal-share-seller" | translate }}</label>
              <ng-select [items]="internalShareConsultants" bindValue="id" bindLabel="name" [clearable]="true"
                placeholder="{{ 't-internal-share-select-seller' | translate }}"
                notFoundText="{{ 't-internal-share-no-sellers' | translate }}" formControlName="internalShareSellerId"
                [readonly]="isLocked || isInternalShareLocked">
              </ng-select>
            </div>
          </div>

          <div class="row g-3">
            <div *ngIf="!isInternalShareMode" class="col-12 col-md-6">
              <label for="partner-name" class="form-label">{{ 't-partner-entity' | translate }}</label>
              <input [attr.disabled]="isLocked ? true : null" type="text" id="partner-name"
                placeholder="{{ 't-partner-entity' | translate }}" class="form-control"
                formControlName="partnerEntityName" />
            </div>
            <div *ngIf="!isRentMode" class="col-12 col-md-6">
              <label for="splitCommission" class="form-label">
                {{ "t-properties-details.t-commission-informed" | translate }}
                <span ngbTooltip="{{ 't-properties-details.t-commission-informed-info' | translate }}"
                  class="ms-1 rounded-circle badge badge-soft-primary">?</span>
              </label>
              <input [attr.disabled]="isLocked ? true : null" *ngIf="form['isPercentageCommission']?.value" type="text"
                class="form-control" suffix="%" id="splitCommission" autocomplete="off" mask="separator"
                thousandSeparator=" " placeholder="{{ 't-properties-details.t-shared-commission' | translate }}"
                formControlName="splitCommissionValue" />
              <input [attr.disabled]="isLocked ? true : null" *ngIf="!form['isPercentageCommission']?.value" type="text"
                class="form-control" id="splitCommissionFixed" suffix=" {{ getSuffix() }}" autocomplete="off"
                mask="separator" thousandSeparator=" "
                placeholder="{{ 't-properties-details.t-shared-commission' | translate }}"
                formControlName="splitCommissionValue" />
            </div>

            <div class="col-12 col-md-6">
              <label for="sharedCommission" class="form-label">
                {{ "t-partner-commission" | translate }}
              </label>
              <input [attr.disabled]="isLocked ? true : null" type="text" id="sharedCommission" mask="separator"
                thousandSeparator=" " suffix="%" placeholder="{{ 't-partner-commission' | translate }}"
                class="form-control" formControlName="sharedCommission" />
            </div>
          </div>
        </div>
      </ng-container>
    </section>

    <!-- Secção: Comissão de referenciação -->
    <section *ngIf="showReferralCommissionSection" class="sale-section">
      <div class="sale-section-head">
        <div class="sale-section-icon"><i class="ri-share-forward-line"></i></div>
        <div class="min-w-0">
          <h6 class="sale-section-title">{{ "t-sale-form-referral-commission-title" | translate }}</h6>
          <p class="sale-section-sub">{{ "t-new-sales.t-referral-commission-sub" | translate }}</p>
        </div>
        <div class="ms-auto" *ngIf="!canEditReferralCommission">
          <span class="badge bg-light text-muted">{{ "t-read-only" | translate }}</span>
        </div>
      </div>

      <div *ngIf="referralCommissionNoticeKey" class="alert alert-info py-2 px-3 mb-3">
        {{ referralCommissionNoticeKey | translate }}
      </div>

      <div class="row g-3">
        <div class="col-12 col-lg-6">
          <label for="referralCommissionValueTypeInput" class="form-label">
            {{ "t-sale-form-referral-commission-type" | translate }}
          </label>
          <select id="referralCommissionValueTypeInput" class="form-select"
            formControlName="referralCommissionValueType"
            [attr.disabled]="!canEditReferralCommission ? true : null">
            <option value="PERCENTAGE">{{ "t-new-sales.t-percentage" | translate }}</option>
            <option value="FIXED">{{ "t-new-sales.t-fixed" | translate }}</option>
          </select>
        </div>

        <div class="col-12 col-lg-6">
          <label for="referralCommissionValueInput" class="form-label">
            {{ "t-sale-form-referral-commission-value" | translate }}
          </label>
          <input id="referralCommissionValueInput" type="text" class="form-control" mask="separator"
            thousandSeparator=" "
            [suffix]="form['referralCommissionValueType']?.value === 'FIXED' ? (' ' + getSuffix()) : ' %'"
            formControlName="referralCommissionValue" [attr.disabled]="!canEditReferralCommission ? true : null" />
        </div>
      </div>

      <small *ngIf="!canEditReferralCommission" class="text-muted d-block mt-2">
        {{ "t-sale-form-referral-commission-readonly" | translate }}
      </small>
    </section>

    <!-- Secção: Consultores envolvidos -->
    <section *ngIf="showAgentSections && (!isExternalProperty || showSellerSection())" class="sale-section">
      <div class="sale-section-head">
        <div class="sale-section-icon"><i class="ri-user-star-line"></i></div>
        <div class="min-w-0">
          <h6 class="sale-section-title">{{ "t-new-sales.t-consultants-title" | translate }}</h6>
          <p class="sale-section-sub">{{ "t-new-sales.t-consultants-sub" | translate }}</p>
        </div>
      </div>

      <div class="row g-3">
        <!-- Angariador(es) -->
        <div *ngIf="!isExternalProperty" class="col-12 col-md">
          <div class="sale-agents-section">
            <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
              <h6 class="text-muted mb-0">{{ "t-new-sales.t-listing-and-commissions" | translate }}</h6>
              <button (click)="onAddAgent('listing')" type="button" *ngIf="!isLocked" class="btn btn-sm btn-soft-primary">
                <i class="ri-user-add-line align-bottom me-1"></i>{{ "t-new-sales.t-add-agent" | translate }}
              </button>
            </div>

            <div class="d-flex flex-column gap-2" *ngIf="selectedListingAgents.length > 0">
              <ng-container *ngFor="let agent of selectedListingAgents; trackBy: trackByAgentId">
                <ng-container *ngTemplateOutlet="agentCard; context: { $implicit: agent, type: 'listing' }"></ng-container>
              </ng-container>
            </div>

            <div class="sale-agents-empty" *ngIf="!selectedListingAgents.length"
              [class.is-warning]="highlightMissingCommissions && !isLocked">
              <i class="align-bottom me-1" [ngClass]="highlightMissingCommissions && !isLocked ? 'ri-alert-line' : 'ri-user-line'"></i>
              {{ (highlightMissingCommissions ? "t-new-sales.t-listing-agent-missing" : "t-new-sales.t-no-agents") | translate }}
            </div>

            <div class="mt-2" *ngIf="openNewAgent?.listing">
              <ng-select bindValue="id" bindLabel="name" (ngModelChange)="onChangeAgent($event, 'listing')"
                [ngModelOptions]="{ standalone: true }" [(ngModel)]="selected.listing" [items]="agentsExceptListingFiltered"
                placeholder="{{ 't-new-sales.t-select-agent' | translate }}">
                <ng-template ng-label-tmp let-agent="item">
                  <div class="d-flex align-items-center">
                    <img [src]="agentAvatar(agent)" (error)="onAvatarError($event)" class="avatar-xxs rounded-circle" alt="" />
                    <span class="fw-medium ps-2">{{ agent?.name }}</span>
                    <span class="text-muted fs-11 ps-2">{{ getStoreById(agent?.store) }}</span>
                  </div>
                </ng-template>
                <ng-template ng-option-tmp let-agent="item">
                  <div class="d-flex align-items-center">
                    <img [src]="agentAvatar(agent)" (error)="onAvatarError($event)" class="avatar-xs rounded-circle" alt="" />
                    <div class="d-flex flex-column ps-3">
                      <span class="fw-medium">{{ agent.name }}</span>
                      <span class="text-muted fs-11">{{ getStoreById(agent?.store) }}</span>
                    </div>
                  </div>
                </ng-template>
              </ng-select>
            </div>
          </div>
        </div>

        <!-- Vendedor(es) -->
        <div *ngIf="showSellerSection()" class="col-12 col-md">
          <div class="sale-agents-section">
            <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
              <h6 class="text-muted mb-0">
                {{ "t-new-sales.t-seller-and-commissions" | translate }}
                <span class="text-danger" *ngIf="requiresSellerForSale()">*</span>
              </h6>
              <button (click)="onAddAgent('selling')" type="button" *ngIf="!isLocked" class="btn btn-sm btn-soft-primary">
                <i class="ri-user-add-line align-bottom me-1"></i>{{ "t-new-sales.t-add-agent" | translate }}
              </button>
            </div>

            <div class="d-flex flex-column gap-2" *ngIf="selectedSellingAgents.length > 0">
              <ng-container *ngFor="let agent of selectedSellingAgents; trackBy: trackByAgentId">
                <ng-container *ngTemplateOutlet="agentCard; context: { $implicit: agent, type: 'selling' }"></ng-container>
              </ng-container>
            </div>

            <div class="sale-agents-empty" *ngIf="!selectedSellingAgents.length"
              [class.is-warning]="highlightMissingCommissions && !isLocked">
              <i class="align-bottom me-1" [ngClass]="highlightMissingCommissions && !isLocked ? 'ri-alert-line' : 'ri-user-line'"></i>
              {{ (highlightMissingCommissions ? "t-new-sales.t-selling-agent-missing" : "t-new-sales.t-no-agents") | translate }}
            </div>

            <div *ngIf="isSellerRequiredInvalid()" class="text-danger small mt-2">
              {{ 't-sale-seller-required' | translate }}
            </div>

            <div class="mt-2" *ngIf="openNewAgent?.selling">
              <ng-select bindValue="id" bindLabel="name" (ngModelChange)="onChangeAgent($event, 'selling')"
                [ngModelOptions]="{ standalone: true }" [(ngModel)]="selected.selling" [items]="agentsExceptSelling"
                placeholder="{{ 't-new-sales.t-select-agent' | translate }}">
                <ng-template ng-label-tmp let-agent="item">
                  <div class="d-flex align-items-center">
                    <img [src]="agentAvatar(agent)" (error)="onAvatarError($event)" class="avatar-xxs rounded-circle" alt="" />
                    <span class="fw-medium ps-2">{{ agent?.name }}</span>
                    <span class="text-muted fs-11 ps-2">{{ getStoreById(agent?.store) }}</span>
                  </div>
                </ng-template>
                <ng-template ng-option-tmp let-agent="item">
                  <div class="d-flex align-items-center">
                    <img [src]="agentAvatar(agent)" (error)="onAvatarError($event)" class="avatar-xs rounded-circle" alt="" />
                    <div class="d-flex flex-column ps-3">
                      <span class="fw-medium">{{ agent.name }}</span>
                      <span class="text-muted fs-11">{{ getStoreById(agent?.store) }}</span>
                    </div>
                  </div>
                </ng-template>
              </ng-select>
            </div>
          </div>
        </div>
      </div>

      <!-- Cartão de agente: avatar · nome/loja · comissão % · remover -->
      <ng-template #agentCard let-agent let-type="type">
        <div class="sale-agent-card" [class.is-missing]="isAgentCommissionMissing(agent, type)">
          <img [src]="agentAvatar(agent)" (error)="onAvatarError($event)" alt="" class="sale-agent-avatar rounded-circle" />
          <div class="flex-fill min-w-0">
            <div class="fw-semibold text-truncate">{{ agent?.name }}</div>
            <small class="text-muted d-block text-truncate">{{ getStoreById(agent?.store) }}</small>
            <small *ngIf="agent?.commissionTargetChangedSinceLastSale"
              class="commission-target-feedback commission-target-feedback--info d-block">
              {{ getTranslatedCommissionTargetChangedMessage(agent) }}
            </small>
            <small *ngIf="agent?.commissionTargetWillChangeOnNextSale"
              class="commission-target-feedback commission-target-feedback--warning d-block">
              {{ getTranslatedCommissionTargetNextSaleMessage(agent) }}
            </small>
          </div>
          <!-- Etiqueta à esquerda do campo, numa linha só: assim o centro do
               cartão coincide com o campo e o botão de remover fica alinhado. -->
          <div class="sale-agent-commission">
            <div class="sale-agent-commission-row">
            <label class="form-label mb-0 text-muted fs-11 text-nowrap">{{ "t-new-sales.t-commission" | translate }}</label>
            <div class="input-group input-group-sm">
              <ng-container *ngIf="type === 'listing'">
                <input [disabled]="isLocked || agent.commissionTargetEnabled" type="text" mask="separator"
                  thousandSeparator=" " [(ngModel)]="agent.listingCommission"
                  (ngModelChange)="onAgentCommissionInputChanged()" [ngModelOptions]="{ standalone: true }"
                  class="form-control text-end" [class.is-invalid]="isAgentCommissionMissing(agent, 'listing')" />
              </ng-container>
              <ng-container *ngIf="type === 'selling'">
                <input [disabled]="isLocked || agent.commissionTargetEnabled" type="text" mask="separator"
                  thousandSeparator=" " [(ngModel)]="agent.saleCommission"
                  (ngModelChange)="onAgentCommissionInputChanged()" [ngModelOptions]="{ standalone: true }"
                  class="form-control text-end" [class.is-invalid]="isAgentCommissionMissing(agent, 'selling')" />
              </ng-container>
              <span class="input-group-text">%</span>
            </div>
            </div>
            <small class="text-danger d-block" *ngIf="isAgentCommissionMissing(agent, type)">
              {{ "t-new-sales.t-commission-missing" | translate }}
            </small>
            <small class="text-muted d-block" *ngIf="agent?.commissionTargetEnabled">
              {{ "t-new-sales.t-commission-by-target" | translate }}
            </small>
          </div>
          <button *ngIf="!isLocked" type="button" class="btn btn-sm btn-soft-danger btn-icon flex-shrink-0"
            ngbTooltip="{{ 't-remove' | translate }}" container="body" (click)="onRemoveAgent(type, agent.id)">
            <i class="ri-close-line"></i>
          </button>
        </div>
      </ng-template>
    </section>

    <!-- Secção: Condições de pagamento -->
    <section *ngIf="!isRentMode" class="sale-section sale-payments">
      <div class="sale-section-head">
        <div class="sale-section-icon"><i class="ri-money-dollar-circle-line"></i></div>
        <div class="min-w-0">
          <h6 class="sale-section-title">{{ "t-new-sales.t-payment-conditions" | translate }}</h6>
          <p class="sale-section-sub">{{ "t-new-sales.t-payment-conditions-hint" | translate }}</p>
        </div>
        <div class="ms-auto">
          <button (click)="onAddPaymentCondition()" type="button" class="btn btn-sm btn-soft-primary text-nowrap">
            <i class="ri-add-line align-bottom me-1"></i>{{ "t-new-sales.t-add-payment" | translate }}
          </button>
        </div>
      </div>

      <div cdkDropList [cdkDropListData]="paymentsConditions" (cdkDropListDropped)="onPaymentsDrop($event)">
        <!-- Cabeçalho das colunas (só desktop; em ecrãs estreitos cada célula tem o seu label) -->
        <div *ngIf="paymentsConditions?.length" class="sale-payment-grid sale-payments-header d-none d-lg-grid">
          <span></span>
          <span class="sale-payments-header-label">
            {{ "t-new-sales.t-payment-moment" | translate }}
            <i class="ri-information-line text-muted ms-1" ngbTooltip="{{ 't-new-sales.t-payment-moment-tooltip' | translate }}" container="body"></i>
          </span>
          <span class="sale-payments-header-label">
            <i class="ri-user-3-line text-info me-1"></i>{{ "t-new-sales.t-buyer-pays" | translate }}
            <i class="ri-information-line text-muted ms-1" ngbTooltip="{{ 't-new-sales.t-buyer-pays-tooltip' | translate }}" container="body"></i>
          </span>
          <span class="sale-payments-header-label">
            <i class="ri-store-2-line text-primary me-1"></i>{{ "t-new-sales.t-agency-commission-stage" | translate }}
            <i class="ri-information-line text-muted ms-1" ngbTooltip="{{ 't-new-sales.t-agency-commission-tooltip' | translate }}" container="body"></i>
          </span>
          <span class="sale-payments-header-label">{{ "t-new-sales.t-estimated-date" | translate }}</span>
          <span></span>
        </div>

        <div class="sale-payment-row sale-payment-grid" cdkDrag *ngFor="let payment of paymentsConditions; let i = index">
          <div class="sale-payment-side cursor-move d-none d-lg-flex" cdkDragHandle>
            <i class="mdi mdi-drag text-muted"></i>
          </div>

          <!-- Momento -->
          <div class="min-w-0">
            <label class="sale-payment-mobile-label d-lg-none">{{ "t-new-sales.t-payment-moment" | translate }}</label>
            <ng-select [(ngModel)]="payment.type" [clearable]="false" [ngModelOptions]="{ standalone: true }"
              [multiple]="false" [searchable]="false">
              <ng-option [value]="'RESERVATION'"><span class="sale-payment-dot bg-warning"></span>{{ "t-new-sales.t-reservation" | translate }}</ng-option>
              <ng-option [value]="'SALE_AGREEMENT'"><span class="sale-payment-dot bg-info"></span>{{ "t-new-sales.t-sale-agreement" | translate }}</ng-option>
              <ng-option [value]="'SIGNAL'"><span class="sale-payment-dot bg-secondary"></span>{{ "t-new-sales.t-signal" | translate }}</ng-option>
              <ng-option [value]="'DEED'"><span class="sale-payment-dot bg-success"></span>{{ "t-new-sales.t-deed" | translate }}</ng-option>
            </ng-select>
          </div>

          <!-- Comprador: valor + toggle %/valor fixo integrado -->
          <div class="min-w-0">
            <label class="sale-payment-mobile-label d-lg-none">{{ "t-new-sales.t-buyer-pays" | translate }}</label>
            <div class="input-group">
              <input type="text" mask="separator" [ngModelOptions]="{ standalone: true }" thousandSeparator=" "
                (input)="onPaymentValueChange(payment)" class="form-control" [(ngModel)]="payment.value" />
              <button type="button" class="btn" [ngClass]="payment.valueType === 'PERCENTAGE' ? 'btn-primary' : 'btn-outline-primary'"
                (click)="payment.valueType = 'PERCENTAGE'; onPaymentValueChange(payment)"
                ngbTooltip="{{ 't-new-sales.t-percentage' | translate }}" container="body">%</button>
              <button type="button" class="btn" [ngClass]="payment.valueType === 'FIXED' ? 'btn-primary' : 'btn-outline-primary'"
                (click)="payment.valueType = 'FIXED'; onPaymentValueChange(payment)"
                ngbTooltip="{{ 't-new-sales.t-fixed' | translate }}" container="body">{{ getSuffix() }}</button>
            </div>
            <!-- <small class="text-muted sale-payment-preview" *ngIf="buyerAmount(payment) !== null">
              <ng-container *ngIf="payment.valueType == 'PERCENTAGE'">= {{ buyerAmount(payment) | number: "1.0-2" }} {{ getSuffix() }}</ng-container>
              <ng-container *ngIf="payment.valueType != 'PERCENTAGE' && buyerPercent(payment) !== null">= {{ buyerPercent(payment) | number: "1.0-2" }} %</ng-container>
            </small> -->
          </div>

          <!-- Agência: valor + toggle % da comissão / valor fixo -->
          <div class="min-w-0">
            <label class="sale-payment-mobile-label d-lg-none">{{ "t-new-sales.t-agency-commission-stage" | translate }}</label>
            <div class="input-group">
              <ng-container *ngIf="agencyMode(payment) === 'PERCENTAGE'">
                <input type="text" mask="separator" [ngModelOptions]="{ standalone: true }" thousandSeparator=" "
                  class="form-control" [(ngModel)]="payment.agencyValue" (input)="onPaymentValueChange(payment)" />
              </ng-container>
              <ng-container *ngIf="agencyMode(payment) === 'FIXED'">
                <input type="text" mask="separator" [ngModelOptions]="{ standalone: true }" thousandSeparator=" "
                  class="form-control" [ngModel]="agencyFixedValue(payment)"
                  (ngModelChange)="onAgencyFixedInput(payment, $event)" />
              </ng-container>
              <button type="button" class="btn" [ngClass]="agencyMode(payment) === 'PERCENTAGE' ? 'btn-primary' : 'btn-outline-primary'"
                (click)="setAgencyMode(payment, 'PERCENTAGE')" ngbTooltip="{{ 't-new-sales.t-agency-mode-percent' | translate }}" container="body">%</button>
              <button type="button" class="btn" [ngClass]="agencyMode(payment) === 'FIXED' ? 'btn-primary' : 'btn-outline-primary'"
                [disabled]="!storeCommissionAmount"
                (click)="setAgencyMode(payment, 'FIXED')" ngbTooltip="{{ 't-new-sales.t-agency-mode-fixed' | translate }}" container="body">{{ getSuffix() }}</button>
            </div>
            <!-- <small class="text-muted sale-payment-preview" *ngIf="agencyMode(payment) === 'PERCENTAGE' && agencyAmount(payment) !== null">
              = {{ agencyAmount(payment) | number: "1.0-2" }} {{ getSuffix() }}
            </small>
            <small class="text-muted sale-payment-preview" *ngIf="agencyMode(payment) === 'FIXED' && parseAmount(payment.agencyValue) !== null">
              = {{ payment.agencyValue }} % {{ "t-new-sales.t-of-commission" | translate }}
            </small> -->
          </div>

          <!-- Data prevista -->
          <div class="min-w-0">
            <label class="sale-payment-mobile-label d-lg-none">{{ "t-new-sales.t-estimated-date" | translate }}</label>
            <input class="form-control flatpickr-input" type="text"
              placeholder="{{ 't-general.t-select' | translate }}" mwlFlatpickr [convertModelValue]="true"
              [dateFormat]="'Y-m-d'" [(ngModel)]="payment.estimatedDate" [ngModelOptions]="{ standalone: true }" />
          </div>

          <div class="sale-payment-side sale-payment-side-end">
            <button (click)="onRemovePaymentCondition(i)" type="button" class="btn btn-sm btn-soft-danger btn-icon"
              ngbTooltip="{{ 't-remove' | translate }}" container="body">
              <i class="ri-delete-bin-line"></i>
            </button>
          </div>
        </div>

        <div class="sale-agents-empty" *ngIf="!paymentsConditions?.length">
          <i class="ri-money-euro-box-line align-bottom me-1"></i>{{ "t-new-sales.t-no-payments" | translate }}
        </div>
      </div>

      <!-- Totais em tiles: comprador deve somar 100% do valor; agência 100% da comissão -->
      <div class="row g-3 mt-1" *ngIf="paymentsConditions?.length">
        <div class="col-12 col-sm-4">
          <div class="sale-summary-tile"
            [ngClass]="buyerTotalOff ? 'sale-summary-tile-warning' : 'sale-summary-tile-primary'">
            <p class="sale-summary-tile-label">
              {{ "t-new-sales.t-total-buyer-pays" | translate }}
              <i class="ri-alert-line ms-1" *ngIf="buyerTotalOff"
                ngbTooltip="{{ 't-new-sales.t-buyer-total-warning' | translate: { value: buyerTotalPercent } }}"
                container="body"></i>
            </p>
            <p class="sale-summary-tile-value">
              <ng-container *ngIf="buyerTotalAmount !== null">{{ buyerTotalAmount | number: "1.0-2" }} {{ getSuffix() }}</ng-container>
              <ng-container *ngIf="buyerTotalAmount === null">—</ng-container>
            </p>
            <p class="sale-summary-tile-sub" *ngIf="buyerTotalPercent !== null">
              {{ buyerTotalPercent | number: "1.0-2" }} % {{ "t-new-sales.t-of-sale-value" | translate }}
            </p>
          </div>
        </div>

        <div class="col-12 col-sm-4">
          <div class="sale-summary-tile"
            [ngClass]="agencyTotalOff ? 'sale-summary-tile-warning' : 'sale-summary-tile-info'">
            <p class="sale-summary-tile-label">
              {{ "t-new-sales.t-total-agency-receives" | translate }}
              <i class="ri-alert-line ms-1" *ngIf="agencyTotalOff"
                ngbTooltip="{{ 't-new-sales.t-agency-total-warning' | translate: { value: agencyTotalPercent } }}"
                container="body"></i>
            </p>
            <p class="sale-summary-tile-value">
              <ng-container *ngIf="agencyTotalAmount !== null">{{ agencyTotalAmount | number: "1.0-2" }} {{ getSuffix() }}</ng-container>
              <ng-container *ngIf="agencyTotalAmount === null">—</ng-container>
            </p>
            <p class="sale-summary-tile-sub" *ngIf="agencyTotalPercent !== null">
              {{ agencyTotalPercent | number: "1.0-2" }} % {{ "t-new-sales.t-of-commission" | translate }}
              <ng-container *ngIf="storeCommissionAmount !== null"> / {{ storeCommissionAmount | number: "1.0-2" }} {{ getSuffix() }}</ng-container>
            </p>
          </div>
        </div>

        <div class="col-12 col-sm-4">
          <div class="sale-summary-tile"
            [ngClass]="buyerTotalDifference === null ? 'sale-summary-tile-muted' : (buyerTotalOff ? 'sale-summary-tile-danger' : 'sale-summary-tile-success')">
            <p class="sale-summary-tile-label">{{ "t-new-sales.t-diff-vs-sale-value" | translate }}</p>
            <p class="sale-summary-tile-value">
              <ng-container *ngIf="buyerTotalDifference !== null">
                {{ buyerTotalDifference | number: "1.0-2" }} {{ getSuffix() }}
                <i class="ri-checkbox-circle-line align-bottom ms-1" *ngIf="!buyerTotalOff"></i>
              </ng-container>
              <ng-container *ngIf="buyerTotalDifference === null">—</ng-container>
            </p>
          </div>
        </div>
      </div>
    </section>

    <div *ngIf="isProposalCheck" class="modal-footer">
      <button type="button" (click)="submitCheckProposal()" class="btn btn-primary w-xs"
        [disabled]="commissionTargetPreviewLoading">
        {{ (isRentMode ? 't-proposals.t-create-rent' : 't-proposals.t-create-sale') | translate }}
      </button>
    </div>
  </div>
</ng-container>

<ng-template #dealPickerModal let-modal>
  <div class="modal-header p-3 ps-4 bg-soft-primary">
    <h5 class="modal-title">{{ 't-sale-form-select-deal' | translate }}</h5>
    <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body">
    <div class="mb-3">
      <input type="text" class="form-control" placeholder="{{ 't-sale-form-search-deal-placeholder' | translate }}"
        [(ngModel)]="dealSearch" [ngModelOptions]="{ standalone: true }" (ngModelChange)="onDealSearchChange($event)" />
    </div>

    <div class="d-flex flex-wrap btn-group mb-3">
      <button type="button" class="btn btn-sm"
        [ngClass]="dealAssociationFilter === 'related' ? 'btn-primary' : 'btn-outline-primary'"
        (click)="setDealAssociationFilter('related')">
        {{ 't-sale-form-related-deals' | translate }}
      </button>
      <button type="button" class="btn btn-sm"
        [ngClass]="dealAssociationFilter === 'unrelated' ? 'btn-primary' : 'btn-outline-primary'"
        (click)="setDealAssociationFilter('unrelated')">
        {{ 't-sale-form-unrelated-deals' | translate }}
      </button>
    </div>

    <div *ngIf="dealStatusOptions.length > 0" class="d-flex flex-wrap btn-group mb-3">
      <button type="button" class="btn btn-sm" [ngClass]="!selectedDealStatusId ? 'btn-info' : 'btn-outline-info'"
        (click)="setDealStatusFilter(null)">
        {{ 't-all-statuses' | translate }}
      </button>
      <button type="button" class="btn btn-sm" *ngFor="let status of dealStatusOptions"
        [ngClass]="selectedDealStatusId === status.id ? 'btn-info' : 'btn-outline-info'"
        (click)="setDealStatusFilter(status.id)">
        {{ status.name | uppercase }}
      </button>
    </div>

    <div class="d-flex justify-content-between align-items-center small text-muted mb-2">
      <span>{{ dealsTotal }} {{ 't-sale-form-deals-found' | translate }}</span>
      <span *ngIf="loadingDeals" class="d-flex align-items-center gap-2">
        <span class="spinner-border spinner-border-sm"></span>
        {{ 't-loading' | translate }}
      </span>
    </div>

    <div class="d-flex flex-column gap-3 deal-picker-list" (scroll)="onDealsScroll($event)">
      <div class="small text-uppercase text-muted fw-semibold mb-0">
        {{ dealAssociationFilter === 'related' ? ('t-sale-form-related-deals' | translate) :
        ('t-sale-form-unrelated-deals' | translate) }}
      </div>
      <ng-container *ngIf="getVisibleDeals().length > 0; else noDealsByScope">
        <ng-container *ngFor="let deal of getVisibleDeals(); trackBy: trackByDealId">
          <ng-container *ngTemplateOutlet="dealOptionTemplate; context: { $implicit: deal }"></ng-container>
        </ng-container>
      </ng-container>

      <ng-template #dealOptionTemplate let-deal>
        <button type="button" class="btn btn-light text-start border rounded-3 p-3 mb-2 deal-option-card"
          (click)="selectDealForSale(deal, modal)">
          <div class="deal-option-row">
            <div class="deal-option-main">
              <div class="fw-semibold deal-option-line">{{ getDealContactName(deal) }}</div>
              <div class="small text-muted deal-option-line">{{ 't-reference' | translate }}: {{
                getDealPropertyRef(deal) }}</div>
              <div class="small text-muted deal-option-line">{{ getDealPropertyTitle(deal) }}</div>
              <div class="small text-muted deal-option-line">{{ getDealContactEmail(deal) }} | {{
                getDealContactPhone(deal) }}</div>
              <div class="small text-muted deal-option-line" *ngIf="getDealReceivedDate(deal)">
                {{ 't-sale-form-received-at' | translate }}: {{ getDealReceivedDate(deal) | date: 'dd/MM/yyyy HH:mm' }}
              </div>
            </div>
            <div class="text-end deal-option-side">
              <span class="badge badge-soft-primary text-uppercase">{{ getDealStatusName(deal) }}</span>
              <span *ngIf="!isDealRelatedToCurrentProperty(deal)" class="badge badge-soft-warning ms-1">
                {{ 't-sale-form-unrelated' | translate }}
              </span>
              <div class="d-flex align-items-center justify-content-end gap-2 mt-2">
                <img [src]="getDealAgentImage(deal)" class="rounded-circle" width="24" height="24" alt="avatar" />
                <span class="small text-muted">{{ getDealAgentName(deal) }}</span>
              </div>
            </div>
          </div>
        </button>
      </ng-template>

      <ng-template #noDealsByScope>
        <div *ngIf="!loadingDeals" class="text-muted text-center py-4">
          {{ dealAssociationFilter === 'related' ? ('t-sale-form-no-related-deals' | translate) :
          ('t-sale-form-no-unrelated-deals' | translate) }}
        </div>
      </ng-template>

      <div *ngIf="loadingDeals && dealsList.length > 0"
        class="d-flex align-items-center justify-content-center gap-2 py-2 text-muted">
        <span class="spinner-border spinner-border-sm"></span>
        {{ 't-loading' | translate }}
      </div>

      <div *ngIf="!loadingDeals && hasMoreDeals" class="text-center text-muted small py-2">
        {{ 't-sale-form-scroll-load-more' | translate }}
      </div>
    </div>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-light" (click)="modal.dismiss()">{{ 't-close' | translate }}</button>
  </div>
</ng-template>
